// src/app/services/shared-workspace.service.ts
//
// The /api/workspace endpoints: shared workspaces, their members, and the
// invitations into them.
//
// These are session-authenticated. postRequest() sends the bearer token and
// nothing else, which is what the server expects here - no api key, no
// signature. A workspace is always named by id: the server resolves the key
// from membership, and a key sent from here would be ignored.

import { Injectable } from '@angular/core';
import { AppService } from '../app.service';

export interface SharedWorkspace {
  workspaceId: number;
  name: string;
  kind: string;
  shareable: boolean;
  default: boolean;
  status: string;
  createdAt: number;
  role?: string;
  memberCount?: number;
}

export interface WorkspaceMember {
  userId: number;
  email: string;
  name?: string;
  avatar?: string;
  role: string;
  access: string;
  joinedAt: number;
}

export interface SentInvite {
  inviteId: number;
  email: string;
  status: string;
  createdAt: number;
  expiresAt: number;
  acceptedAt?: number;
}

export interface PendingInvite {
  inviteId: number;
  workspaceId: number;
  workspaceName: string;
  invitedBy: string;
  expiresAt: number;
}

export interface WorkspaceList {
  owned: SharedWorkspace[];
  sharedWithMe: SharedWorkspace[];
}

@Injectable({ providedIn: 'root' })
export class SharedWorkspaceService {

  constructor(private app: AppService) { }

  // ── workspaces ──────────────────────────────────────────────────

  createWorkspace(name: string): Promise<SharedWorkspace> {
    return this.call<SharedWorkspace>('create', { name });
  }

  listWorkspaces(): Promise<WorkspaceList> {
    return this.call<WorkspaceList>('list', {});
  }

  // Turning an existing personal workspace into a shared one. The server
  // refuses the first time when the workspace already holds memories and says
  // how many, so the confirmation can name a real number instead of asking
  // about an unknown quantity. The thrown error carries that through.
  async convertToShared(workspaceId: number, confirm = false): Promise<SharedWorkspace> {
    try {
      const res: any = await this.app.postRequest(
        { workspaceId, confirm }, 'workspace/convert');
      return res?.data as SharedWorkspace;
    } catch (error: any) {
      const details = error?.error?.details || {};
      const wrapped: any = new Error(this.messageOf(error));
      wrapped.requiresConfirmation = !!details.requiresConfirmation;
      wrapped.itemCount = details.itemCount;
      throw wrapped;
    }
  }

  // ── invitations ─────────────────────────────────────────────────

  invite(workspaceId: number, email: string): Promise<any> {
    return this.call<any>('invite', { workspaceId, email });
  }

  listInvites(workspaceId: number): Promise<SentInvite[]> {
    return this.call<SentInvite[]>('invites', { workspaceId });
  }

  revokeInvite(inviteId: number): Promise<any> {
    return this.call<any>('invite/revoke', { inviteId });
  }

  myInvites(): Promise<PendingInvite[]> {
    return this.call<PendingInvite[]>('invite/mine', {});
  }

  // Both accept and decline take either an inviteId (from your own pending
  // list) or a token (from the emailed link). The server checks the same
  // thing either way: that the invitation was addressed to you.
  viewInvite(ref: { token?: string; inviteId?: number }): Promise<any> {
    return this.call<any>('invite/view', ref);
  }

  acceptInvite(ref: { token?: string; inviteId?: number }): Promise<any> {
    return this.call<any>('invite/accept', ref);
  }

  declineInvite(ref: { token?: string; inviteId?: number }): Promise<any> {
    return this.call<any>('invite/decline', ref);
  }

  // ── members ─────────────────────────────────────────────────────

  listMembers(workspaceId: number): Promise<WorkspaceMember[]> {
    return this.call<WorkspaceMember[]>('members', { workspaceId });
  }

  // Omit userId to leave the workspace yourself.
  removeMember(workspaceId: number, userId?: number): Promise<any> {
    return this.call<any>('member/remove', userId ? { workspaceId, userId } : { workspaceId });
  }

  // ── sharing an item in ──────────────────────────────────────────

  // workspaceIds is the COMPLETE set this item should end up shared into, not
  // an addition - XDB's shareMemory takes the whole key list. An empty array
  // unshares it everywhere.
  shareItem(payload: {
    queryHash: string;
    sourceWorkspaceId: number;
    userKey: string;
    workspaceIds: number[];
    type: 'MEMORY' | 'RECIPE';
    // First line of the item, stored with the share so the activity digest can
    // name what was added rather than only counting it.
    title?: string;
  }): Promise<any> {
    return this.call<any>('share', payload);
  }

  // ── reading a shared workspace ──────────────────────────────────

  listItems(workspaceId: number, type: 'MEMORY' | 'RECIPE',
            page = 0, pageSize = 25): Promise<any> {
    return this.call<any>('memories', { workspaceId, type, page, pageSize });
  }

  // ── plumbing ────────────────────────────────────────────────────

  private async call<T>(method: string, body: any): Promise<T> {
    try {
      const res: any = await this.app.postRequest(body, `workspace/${method}`);
      return res?.data as T;
    } catch (error: any) {
      throw new Error(this.messageOf(error));
    }
  }

  // The server puts the useful sentence in `details` and a short label in
  // `message` - "Only a workspace created for sharing can have members" versus
  // "This workspace cannot be shared". Prefer the one that tells you what to
  // do about it.
  private messageOf(error: any): string {
    const payload = error?.error;
    if (!payload) {
      return error?.message || 'Something went wrong. Please try again.';
    }
    const details = payload.details;
    if (details && typeof details === 'object') {
      const first = Object.values(details).find(v => typeof v === 'string' && v.length);
      if (first) {
        return first as string;
      }
    }
    return payload.message || 'Something went wrong. Please try again.';
  }
}
